import { ARRANGEMENT_LIMITS, DRAFT_MAX_AGE_DAYS, readableDate } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §2–§4] THE TEMPLATE PAGES' OWN WORDS — in one place, so a test can assert them and a
 *  tidy-up cannot delete one silently. Vocabularies over a shared tuple (readiness, decision class,
 *  run outcome) are in `lib/labels.ts`, where every other enum's words are.
 *
 *  ⚠️ "TEMPLATE", NEVER "BOOKLET ORDER". A template is READ-ONLY for ever — imported once and never
 *  edited — and what it produces is a DRAFT the CP office reviews; every sentence below says which of
 *  the two it is about, because "this order" would be ambiguous on every one of these screens.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const TEMPLATES_COPY = {
  setsTitle: 'Templates',
  noSets: 'No template set has been imported yet.',
  noSetsBody: 'A set is imported from its booklet by an administrator, from a console.',
  /**
   * [DP2026] A set is named by its BOOKLET'S YEAR, with the PA number beneath — two booklets of one event
   * now stand side by side. The PA number is stored as printed ("P.A. No. 1938"), so no prefix is added.
   */
  setYear: (year: number): string => `${String(year)} booklet`,
  setSource: (set: {
    readonly title: string;
    readonly templateCount: number;
    readonly source: { readonly paNumber: string | null; readonly paDate: string | null };
  }): string => {
    const date = set.source.paDate === null ? null : (readableDate(set.source.paDate) ?? set.source.paDate);
    const pa = [set.source.paNumber, date].filter((part): part is string => part !== null).join(' of ');
    return [pa, set.title, `${String(set.templateCount)} templates`].filter((part) => part !== '').join(' · ');
  },
  /** [DP2026] `?publish=` with several booklets of the event: a run publishes only its own set's drafts. */
  publishPick: 'Drafts are published from the booklet that made them. Pick the booklet.',
  noTypeForEvent: 'No arrangement type is registered for this event, so nothing can be created from it yet.',
  noTemplate: 'That template is not in this set. Pick one from the list.',
  /** [TEMPLATE-FIND] `?t=` named a number the set does not hold — never silently the first template. */
  notInSet: (key: string): string =>
    `There is no template ${key} in this set. Type its number in the filter, or pick one from the list.`,
} as const;

export const LIBRARY_COPY = {
  /** [P7B.1 §2] The review site's own heading and lede — words the policing team already read. */
  title: 'Template library',
  lede: 'One template per complex, pandal or division list, grouped by Part, section and division as the booklet prints them.',
  festivalDates: 'Festival dates',
  decisions: 'Decisions',
  plan: 'Plan & create',
  publish: 'Publish drafts',
  selectAllReady: 'Select all ready',
  clearSelection: 'Clear',
  selected: (count: number): string => `${String(count)} selected`,
  year: 'For',
  noTemplates: 'This set has no templates.',
  /** ⚠️ [P7B §3e] Grouped, never a list of conflicts — the decisions page's summary (`DecisionSummary`). */
  decisionsHeadline: (outstanding: number, templates: number): string =>
    outstanding === 0
      ? 'Every decision is made.'
      : `${String(outstanding)} decision${outstanding === 1 ? '' : 's'} affect${outstanding === 1 ? 's' : ''} ${String(
          templates,
        )} template${templates === 1 ? '' : 's'}.`,
  classRow: (decided: number, outstanding: number, templates: number): string =>
    `${String(decided)} decided · ${String(outstanding)} outstanding · ${String(templates)} template${
      templates === 1 ? '' : 's'
    } affected`,
  decideClass: 'Decide',
  /* The tree. */
  browse: (count: number): string => `Browse all ${String(count)} templates`,
  treeLabel: 'Templates',
  filterLabel: 'Filter templates',
  /** [TEMPLATE-FIND] A number — `t152`, `T152` or `152` — finds exactly that template; words match as before. */
  filterPlaceholder: 'Number, title, section or division',
  /** [TEMPLATE-FIND] Appended to the count while exactly one template matches the typed text. */
  enterOpens: 'Enter opens it',
  count: (shown: number, total: number, filtering: boolean): string =>
    filtering ? `${String(shown)} of ${String(total)} match` : `${String(total)} templates`,
  stateFilterLabel: 'Show',
  stateFilter: {
    ALL: 'All',
    BLOCKED: 'Blocked',
    READY: 'Ready',
    CREATED: 'Already created',
    NOT_BUILT: 'Not built',
  },
  columnFilterLabel: 'Columns',
  columnFilter: { ALL: 'All', USUAL: 'Usual', UNUSUAL: 'Unusual' },
  filterCount: (label: string, count: number): string => `${label} ${String(count)}`,
  part: (part: string): string => `Part ${part}`,
  noDivision: 'No division',
  noMatch: 'No templates match this filter.',
  select: (title: string): string => `Select ${title}`,
  /* [P7B.1 §3e] A row's line of words. */
  waitsOn: (words: readonly string[]): string => {
    const shown = words.slice(0, 3);
    const rest = words.length - shown.length;
    if (rest > 0) return `waits on ${shown.join(', ')} and ${String(rest)} more`;
    if (shown.length <= 1) return `waits on ${shown.join('')}`;
    return `waits on ${shown.slice(0, -1).join(', ')} and ${shown.at(-1) ?? ''}`;
  },
  waitWord: {
    blankSource: 'a source the booklet left blank',
    policy: 'a policy',
    inCharge: 'in-charge roles',
    totals: 'a printed total',
    schedule: 'days and times',
    dutyPoint: 'a duty point',
  },
  produced: (number: string, more: number): string =>
    more > 0 ? `${number} and ${String(more)} more part${more === 1 ? '' : 's'}` : number,
  unusualColumn: 'unusual column',
} as const;

/**
 * ⚠️ [P7B.1 §3a] THE PANEL ABOVE THE LIBRARY — every open decision, by how many templates it holds back.
 * The figures are counts of TEMPLATES, never of people; a decision is made once, on the decisions page.
 */
export const OPEN_DECISIONS_COPY = {
  title: 'What holds templates back',
  /** [P7C.1 §2] The link above the library that opens the panel's dialog — the set's own outstanding count. */
  link: (outstanding: number): string =>
    `What holds templates back — ${
      outstanding === 0 ? 'nothing is open' : `${String(outstanding)} decision${outstanding === 1 ? '' : 's'} open`
    }`,
  lead: (ready: number, total: number, notBuilt: number): string =>
    `${ready === 0 ? `None of the ${String(total)} templates can be created yet` : `${String(ready)} of the ${String(total)} templates can be created`}; ${String(notBuilt)} ${notBuilt === 1 ? 'is' : 'are'} never built.`,
  open: (open: number, holdingBack: number): string =>
    `${String(open)} decision${open === 1 ? ' is' : 's are'} open, ${String(holdingBack)} of them holding templates back. Each line says how many templates wait on it.`,
  noneAlone:
    'No single decision makes a template ready on its own yet — each also waits on its days and times, and on a policy for its columns.',
  heldBack: (count: number): string => `holds back ${String(count)} template${count === 1 ? '' : 's'}`,
  onlyFor: (count: number): string => `the only open decision for ${String(count)}`,
  showAll: (count: number): string => `Show all ${String(count)}`,
  showFewer: 'Show fewer',
  decide: 'Decide',
  createPostType: 'Create a post type',
  waiting: {
    POLICY: (count: number): string =>
      `${String(count)} template${count === 1 ? '' : 's'} also need${count === 1 ? 's' : ''} a policy once ${count === 1 ? 'its' : 'their'} columns are decided.`,
    IN_CHARGE_ROLE: (count: number): string =>
      `${String(count)} template${count === 1 ? '' : 's'} also need${count === 1 ? 's' : ''} in-charge roles mapped once a policy is chosen.`,
  },
  unusualTitle: (count: number): string =>
    `${String(count)} template${count === 1 ? ' uses' : 's use'} a column almost no other template uses — each needs its own look`,
  unusualColumns: (columns: readonly string[]): string => `unusual: ${columns.join(', ')}`,
  inChargeTitle: (count: number): string =>
    `${String(count)} code${count === 1 ? ' is' : 's are'} named only as an in-charge's unit and hold${count === 1 ? 's' : ''} nothing back`,
  inChargeRow: (count: number): string =>
    `named as an in-charge unit on ${String(count)} template${count === 1 ? '' : 's'}`,
  legend:
    'Not in PAMS — no unit or post type has this code yet · Probable — the review suggests a match · Role — a role, not a unit',
  everythingDecided: 'Nothing is open — every decision is made.',
  /** [P7C §4] Opens the decision in place, in the library's own dialog; "Decide" still opens the page. */
  preview: 'Preview',
  previewLabel: (words: string): string => `Preview ${words}`,
  words: {
    source: (code: string): string => `Source ${code}`,
    blankSource: 'Force rows with no source printed',
    column: (column: string): string => `Column ${column}`,
    policy: (columns: string): string => `A policy for ${columns}`,
    role: (role: string): string => `In-charge role “${role}”`,
    totals: (title: string): string => `A printed total in ${title}`,
    schedule: (section: string): string => `Days and times for ${section}`,
    /**
     * [TEMPLATE-FIND] `words` is the label's part after ` · ` — `(b)` for a printed serial, else
     * `table 1, row 34` — so two duty points of one template no longer read alike.
     */
    dutyPoint: (title: string, words: string | null): string =>
      words === null
        ? `A duty point in ${title}`
        : words.startsWith('(')
          ? `Duty point ${words} in ${title}`
          : words.startsWith('table ')
            ? `Duty point at ${words} in ${title}`
            : `A duty point in ${title}`,
  },
  chip: {
    notInPams: 'Not in PAMS',
    notMatched: 'Not matched',
    probable: 'Probable',
    mappedInReview: 'Mapped in the review',
    policy: 'Policy',
    role: 'Role',
    differs: 'Differs',
    proposed: 'Proposed',
    schedule: 'Days and times',
    dutyPoint: 'Duty point',
  },
} as const;

/** [P7B.1 §2] A Part's pane and a section's pane — the review site's `partDetail` and `subDetail`. */
export const PANE_COPY = {
  pages: (pages: readonly number[]): string => {
    if (pages.length === 0) return '';
    const low = Math.min(...pages);
    const high = Math.max(...pages);
    return low === high ? `Page ${String(low)}` : `Pages ${String(low)}–${String(high)}`;
  },
  templates: (count: number): string => `${String(count)} template${count === 1 ? '' : 's'}`,
  dutyPoints: (count: number): string => `${String(count)} duty point${count === 1 ? '' : 's'}`,
  sectionsTitle: 'Sections',
  section: 'Section',
  covers: 'Covers',
  pagesColumn: 'Pages',
  templatesColumn: 'Templates',
  dutyPointsColumn: 'Duty points',
  forcePart: 'Force in this Part',
  forceSection: 'Force in this section',
  forceCaption: 'Every duty point that will be built, as the booklet reads — round the clock is left out.',
  printed: 'Printed with this section (as the import kept it)',
  scheduleTitle: 'Days and times',
  scheduleDecided: (days: string): string => `Decided: ${days}`,
  scheduleProposed: (words: string): string => `Proposed from the booklet: ${words}`,
  scheduleNone: 'Not decided, and the booklet gave nothing the import could read.',
  scheduleNotAsked: 'Nothing in this section is built, so its days and times are not asked.',
  decide: 'Decide',
  change: 'Change',
  templatesTitle: 'Templates in this section',
  template: 'Template',
  division: 'Division',
  state: 'State',
  toDecide: 'To decide',
  toDecideCount: (count: number): string => (count === 0 ? '—' : `${String(count)} to decide`),
  foldedIn: 'A section’s command, information and instructions are shown inside each of its templates.',
  noTables: 'This section has no duty-point tables.',
  noPart: 'That Part is not in this set.',
  noSection: 'That section is not in this set.',
} as const;

/** "1 section" or "2 sections" — a count and its noun, in the guide's plain sentences. */
const counted = (count: number, one: string, many: string): string => `${String(count)} ${count === 1 ? one : many}`;
const waitOn = (templates: number, things: number): string =>
  `${counted(templates, 'template waits', 'templates wait')} on ${things === 1 ? 'it' : 'them'}`;

/**
 * ⚠️ [P7C §4] THE LIBRARY'S PREVIEW OF ONE DECISION — the decisions page's own form, in a dialog, and
 * for a column with no exact match, the one question that usually settles it first.
 */
export const DECISION_MODAL_COPY = {
  /** The column's likely post type — the server's suggestion — as a question, never as a decision. */
  likely: (name: string, shortName: string | null): string =>
    `Looks like ${name}${shortName !== null && shortName !== '' && shortName !== name ? ` (${shortName})` : ''} — use it?`,
  likelyVehicle: (name: string): string => `Looks like the vehicle type ${name} — use it?`,
  likelyWhy: 'Matched on the column’s words, not its exact name. Use it only if it is the same post.',
  useLikely: 'Use it',
  createInstead: 'No — create a post type for this column',
  noLikely: (column: string): string => `No post type in PAMS matches “${column}”.`,
  createBody:
    'Create one for it: the post type list opens with the column’s words filled in. Once it is saved you come back to this column on the Decisions page.',
  create: 'Create a post type for this column',
  otherAnswer: 'Choose another answer',
  gone: 'This decision is no longer open — it may have been made elsewhere.',
  openPage: 'Open the Decisions page',
} as const;

/**
 * ⚠️ [P7C §3] THE SETUP GUIDE — the steps in the order that makes each next one possible, every figure
 * this set's own and LIVE. Short sentences; the library's own button labels are interpolated, never
 * retyped, so a renamed button cannot leave the guide pointing at a word nobody can find.
 */
export const SETUP_GUIDE_COPY = {
  trigger: 'Setup guide',
  title: 'Getting templates ready, step by step',
  intro:
    'Every template in this set becomes a draft order once everything it uses is decided. Work through the steps below in this order — each one makes the next possible. The figures are this set’s own, as it stands now.',
  noSingleDecision:
    "No single decision makes a template ready. Every template also waits on its section's days and times and on a policy for its columns, so a template turns Ready only when the last of these is decided.",
  steps: 'Steps',
  SCHEDULE: {
    title: 'Days and times',
    body: 'Each section of the booklet runs on certain festival days, at certain times. For every section, tick the days it runs on, then choose a whole day with a reporting time, or up to four shifts. Where the import could read the booklet’s own wording, the days and times are already proposed — check them and save.',
    link: 'Decide days and times',
    count: (sections: number, templates: number): string =>
      `${counted(sections, 'section still needs', 'sections still need')} days and times; ${waitOn(templates, sections)}.`,
    done: 'Every section has its days and times.',
  },
  FORCE_COLUMN: {
    title: 'Force columns',
    body: 'Each column of the booklet’s force tables names a kind of person a duty point asks for. Match each one to a post type (or a vehicle type) in PAMS, make it a one-off column, or leave it out. A column that matches a post type’s code, name or short name is matched for you; where its words only look like one, that post type is suggested. If the right post type does not exist yet, a super admin can create it from the column’s own form.',
    link: 'Match the columns',
    count: (columns: number, templates: number): string =>
      `${counted(columns, 'column is', 'columns are')} not matched yet; ${waitOn(templates, columns)}.`,
    done: 'Every column is matched.',
  },
  POLICY: {
    title: 'Policies',
    body: 'A duty point is built against a policy, and the policy must carry every column the duty point asks for. Once all of a duty point’s columns are decided, you are asked which policy to use — one that covers them is suggested. Then each in-charge the booklet names is matched to a role on that policy.',
    link: 'Choose the policies',
    count: (sets: number, templates: number): string =>
      `${counted(sets, 'set of columns needs', 'sets of columns need')} a policy now; ${waitOn(templates, sets)}.`,
    done: 'No set of columns is waiting for a policy now.',
    roles: (roles: number): string => `${counted(roles, 'in-charge role', 'in-charge roles')} still to match.`,
  },
  SOURCE: {
    title: 'Sources',
    body: 'Each force row says who sends the people — a division, a police station or a battalion. Match each source to its unit in PAMS, or leave its rows out. A source whose short code matches a unit is matched for you.',
    link: 'Match the sources',
    count: (sources: number, templates: number): string =>
      `${counted(sources, 'source is', 'sources are')} not matched yet; ${waitOn(templates, sources)}.`,
    done: 'Every source is matched.',
    /** Only the parts that are not zero — a sentence naming "0 printed totals" asks nothing. */
    alongTheWay: (totals: number, dutyPoints: number): string => {
      const parts = [
        ...(totals > 0
          ? [`${counted(totals, 'printed total that differs', 'printed totals that differ')} from the rows read`]
          : []),
        ...(dutyPoints > 0
          ? [`${counted(dutyPoints, 'duty point that cannot', 'duty points that cannot')} be built as read`]
          : []),
      ];
      return parts.length === 0 ? '' : `Along the way the Decisions page also asks about ${parts.join(' and ')}.`;
    },
  },
  CREATE: {
    title: 'Create the drafts',
    body: (year: number | null, selectAllReady: string, plan: string): string =>
      `First set the festival dates for ${year === null ? 'the year' : String(year)} — the date each festival day falls on that year. Then tick the Ready templates in the library, or use ${selectAllReady}, and choose ${plan}. You see exactly what will be created before anything is written, and nothing is created until you approve.`,
    link: (year: number): string => `Set the festival dates for ${String(year)}`,
    ready: (count: number): string =>
      count === 0
        ? 'No template is ready yet.'
        : `${counted(count, 'template is', 'templates are')} ready and not yet created.`,
    undated: (undated: number, days: number, year: number): string =>
      undated === 0
        ? `Every festival day has a ${String(year)} date.`
        : `${String(undated)} of the ${counted(days, 'festival day', 'festival days')} ${undated === 1 ? 'has' : 'have'} no ${String(year)} date yet.`,
    readingDates: 'Reading the festival dates…',
  },
  PUBLISH: {
    title: 'Publish',
    body: (publish: string): string =>
      `What you create are ordinary drafts. Review each one, name an officer for every in-charge role, then publish them together on ${publish}. Publishing creates the duties and asks each division to name its officers. A draft that is not published within ${String(DRAFT_MAX_AGE_DAYS)} days of being created is deleted.`,
    drafts: (count: number): string =>
      count === 0
        ? 'No draft is waiting to be published.'
        : `${counted(count, 'draft waits', 'drafts wait')} to be published.`,
  },
} as const;

export const DECISIONS_COPY = {
  title: 'Decisions',
  parent: 'Templates',
  usedBy: (count: number): string => `Used by ${String(count)} template${count === 1 ? '' : 's'}`,
  /** [TEMPLATE-FIND] The templates past the six a row names, as plain text — no list of them exists to link. */
  usedByMore: (count: number): string => `and ${String(count)} more`,
  /**
   * [TEMPLATE-FIND] Each template number on a row is a link; this is its accessible name. ⚠️ It STARTS with
   * the visible number (WCAG 2.5.3, row 1010's rule).
   */
  openTemplate: (key: string): string => `${key}: open this template`,
  /** [TEMPLATE-FIND] A printed total's or a duty point's ONE link — to its own check or row, highlighted. */
  showInTemplate: (key: string): string => `Show it in template ${key}`,
  /**
   * [TEMPLATE-FIND] That link's accessible name: its words, the row's label, and — where the label alone can name
   * two places — what tells them apart. Two printed totals on one page share a label (`t36 · page 78`), and so
   * do two duty points under one repeated serial; `where` is the rows a total adds up, or the duty point's
   * table and row.
   */
  showInTemplateLabel: (key: string, label: string, where: string | null): string =>
    `Show it in template ${key}: ${label}${where === null ? '' : `, ${where}`}`,
  /** [TEMPLATE-FIND] A printed total's `where` in that name: the rows its Total adds up. */
  addingUp: (rows: string): string => `adding up ${rows}`,
  suggestion: 'Suggested',
  decided: 'Decided',
  outstanding: 'Not decided',
  byRule: 'matched by code',
  by: (name: string, at: string | null): string => (at ? `by ${name}, ${at}` : `by ${name}`),
  problems: 'This choice no longer holds',
  decide: 'Decide',
  change: 'Change',
  cancel: 'Cancel',
  save: 'Save decision',
  useSuggestion: 'Use the suggestion',
  note: 'Note (optional)',
  empty: 'Nothing to decide in this class.',
  saved: 'Decision saved',
  /* SOURCE */
  sourceOrgUnit: 'Provided by',
  sourceSkip: 'Leave these rows out',
  sourcePeople: (people: number): string => `${String(people)} people asked of this source`,
  sourceUsedFor: { FORCE: 'Force source', IN_CHARGE: 'In-charge unit', BOTH: 'Force source and in-charge unit' },
  bundleStatus: (status: string): string => `Extraction read it as ${status}`,
  pickUnit: 'Choose the unit',
  /* FORCE_COLUMN */
  columnPostType: 'A post type',
  columnVehicleType: 'A vehicle type',
  columnOneOff: 'A one-off column',
  columnSkip: 'Leave this column out',
  columnPeople: (people: number): string => `${String(people)} people in this column`,
  oneOffLabel: 'Column label',
  restrictTo: 'Only these posts may fill it (optional)',
  /* POLICY */
  policy: 'Policy',
  policyColumns: 'Columns',
  policyDutyPoints: (count: number): string => `${String(count)} duty point${count === 1 ? '' : 's'}`,
  policyDoesNotCover: 'Its current version does not carry every column',
  policyNoVersion: 'Not yet published',
  createPolicy: 'Create a policy covering these',
  /* FORCE_COLUMN · [P7B.1 §3b] — a post type is a master; creating one is a super admin's own act. */
  createPostType: 'Not in the list? Create a post type for this column',
  createPostTypeRefused: 'Not in the list? Only a super admin can add a post type.',
  bulkTitle: 'Use one policy for every set it covers',
  bulkPick: 'Policy',
  bulkApply: 'Use this policy for every set it covers',
  bulkNone: 'That policy covers none of the column sets still to decide.',
  bulkConfirmTitle: (name: string, count: number): string =>
    `Use ${name} for ${String(count)} column set${count === 1 ? '' : 's'}?`,
  bulkConfirmBody: 'Only column sets that are still undecided and that this policy carries in full are changed.',
  bulkDone: (count: number): string => `${String(count)} column set${count === 1 ? '' : 's'} decided`,
  /* IN_CHARGE_ROLE */
  roleOnPolicy: (policy: string): string => `A role on ${policy}`,
  roleOneOff: 'A one-off role',
  roleDrop: 'Drop it',
  roleLabel: 'Role label',
  /* TOTALS */
  totalsAsRead: 'Use the rows as read',
  totalsPrinted: 'Correct the rows to the printed total',
  totalsHold: 'Hold the template back',
  totalsSum: (sum: number, printed: number): string => `Rows sum to ${String(sum)} of ${String(printed)}`,
  totalsMustSum: 'Each corrected column must sum to its printed total before it can be saved.',
  /** ⚠️ The server applies one corrected figure to EVERY row a duty point holds in that column. */
  totalsEachRow: (rows: number): string => `applies to each of its ${String(rows)} rows`,
  totalsInCharge: (count: number): string => `including ${String(count)} in the in-charge cells`,
  /** [TEMPLATE-FIND] Which rows the printed Total adds up — the rows a person checks on the page. */
  totalsCovers: (page: number | null, rows: string): string =>
    `The booklet’s Total row${page === null ? '' : ` on page ${String(page)}`} adds up ${rows}.`,
  totalsCoversUnknown: 'PAMS could not tell which rows this Total row adds up.',
  /**
   * [TEMPLATE-FIND] A column that differs, in words as well as weight: `THG: printed 18, read 22 — 4 more than
   * printed`. ⚠️ The in-charge note qualifies the figure READ, which counts the in-charge cells — never the
   * difference: `Inspector: printed 3, read 4 (4 of them in the in-charge cells) — 1 more than printed`.
   */
  totalsDiffers: (column: string, printed: number, parsed: number, inCharge: number): string =>
    `${column}: printed ${String(printed)}, read ${String(parsed)}${
      inCharge > 0 ? ` (${String(inCharge)} of them in the in-charge cells)` : ''
    } — ${String(Math.abs(parsed - printed))} ${parsed > printed ? 'more' : 'fewer'} than printed`,
  /** [TEMPLATE-FIND] The columns that agree, folded onto one line: `Matches the booklet: Inspector 4, SI/Sgt 8`. */
  totalsMatch: (columns: readonly string[]): string => `Matches the booklet: ${columns.join(', ')}`,
  totalsNotPrinted: (columns: readonly string[]): string => `No printed figure: ${columns.join(', ')}`,
  totalsUncorrectable: 'Only the rows as read, or holding the template back, can settle this one.',
  page: (page: number | null): string => (page === null ? '' : `page ${String(page)}`),
  /* SCHEDULE */
  scheduleDays: 'Festival days',
  scheduleWholeDay: 'Whole day',
  scheduleShifts: 'Shifts',
  scheduleReporting: 'Reporting time (optional)',
  scheduleShift: (index: number): string => `Shift ${String(index + 1)}`,
  scheduleAddShift: 'Add a shift',
  scheduleRemoveShift: 'Remove',
  scheduleStart: 'Start',
  scheduleEnd: 'End',
  scheduleNextDay: 'On the next calendar day',
  scheduleNextDayHint: 'Moves every date on by one — for a duty that starts at midnight after the festival day.',
  scheduleSource: 'The booklet says',
  roundTheClock: 'The booklet calls this section round the clock. It is flagged and nothing is built for it.',
  timeHint: 'HH:mm',
  invalidTime: 'Enter a time as HH:mm, for example 08:00 or 22:30.',
  /* DUTY_POINT */
  dpPlace: 'Type the place',
  dpSkip: 'Leave this duty point out',
  dpIssue: {
    MISSING: 'The booklet gives no place.',
    DAMAGED: 'The place was damaged when names were removed.',
    /* [PLACE-500] The flag was set at import, against 200; an order now takes up to 500. True at both lengths. */
    TOO_LONG: `The place is longer than 200 characters. An order now takes up to ${String(ARRANGEMENT_LIMITS.PLACE_NAME)}: keep it as read if it fits, or shorten it.`,
    NO_FORCE: 'It asks for no force, and publish refuses a duty point that asks for nobody.',
  },
  placeLabel: 'Place',
  placeTooLong: `A place can be at most ${String(ARRANGEMENT_LIMITS.PLACE_NAME)} characters. Shorten it, or leave the duty point out.`,
} as const;

export const CALENDAR_COPY = {
  title: (year: number): string => `Festival dates for ${String(year)}`,
  dayColumn: 'Day',
  sourceColumn: (year: number): string => `In ${String(year)}`,
  dateColumn: (year: number): string => `In ${String(year)}`,
  fillFrom: 'Fill the others from this day',
  fillHint:
    'Sets every other day at its distance from this one in the booklet. Each day can still be changed on its own.',
  save: 'Save dates',
  saved: 'Festival dates saved',
  undated: (count: number): string =>
    `${String(count)} day${count === 1 ? ' is' : 's are'} not dated yet. Drafts are only created for dated days.`,
  noType: (year: number): string =>
    `No arrangement type is registered for ${String(year)}, so drafts cannot be created for that year yet.`,
  typeIs: (label: string): string => `Orders created for this year are listed under ${label}.`,
  updated: (name: string, at: string): string => `Last saved by ${name}, ${at}`,
  sameDate: 'Two days may share a date.',
} as const;

export const CREATE_COPY = {
  title: 'Plan and create drafts',
  nothingSelected: 'Choose the templates to plan on the library page first.',
  planning: 'Working out what will be created…',
  nothingWritten: 'Nothing is written until you approve.',
  draftAge:
    'The drafts are ordinary drafts: a draft that is not published within 30 days of being created is deleted automatically.',
  totals: (orders: number, points: number, duties: number, slots: number): string =>
    `${String(orders)} draft${orders === 1 ? '' : 's'} · ${String(points)} duty points · ${String(duties)} duties · ${String(
      slots,
    )} positions`,
  forYear: (label: string, year: number, paDate: string): string =>
    `${label} for ${String(year)}, dated ${readableDate(paDate) ?? paDate}`,
  itemsTitle: 'Will be created',
  itemSize: (points: number, duties: number, slots: number): string =>
    `${String(points)} duty point${points === 1 ? '' : 's'} · ${String(duties)} dut${duties === 1 ? 'y' : 'ies'} · ${String(
      slots,
    )} position${slots === 1 ? '' : 's'}`,
  part: (part: number, parts: number): string => (parts > 1 ? `part ${String(part)} of ${String(parts)}` : ''),
  inChargeEmpty: (roles: readonly string[]): string =>
    roles.length === 0 ? 'No in-charge roles' : `In-charge, named by nobody yet: ${roles.join(', ')}`,
  blockedTitle: 'Held back — not ready',
  roundTheClockTitle: 'Round the clock — not built',
  wholeTemplate: 'The whole template',
  /** [TEMPLATE-FIND] `'; '`, never `', '` — a duty point with no serial reads `table 1, row 4`, comma and all. */
  excluded: (points: readonly string[]): string => `Duty points left out: ${points.join('; ')}`,
  producedTitle: 'Already produced',
  producedBody: 'These templates already produced an order. Creating them again needs your acknowledgement for each.',
  acknowledge: 'Create it again anyway',
  discardFirst: 'Discard the existing draft first',
  discardConfirmTitle: 'Discard the existing draft?',
  discardConfirmBody: 'It is moved to the Archive and can be restored for 30 days. The plan is then worked out again.',
  droppedTitle: 'People left out',
  droppedWhy: {
    SOURCE_SKIPPED: 'its source is left out',
    COLUMN_SKIPPED: 'its column is left out',
    DUTY_POINT_SKIPPED: 'its duty point is left out',
    UNPARSED: 'the booklet cell could not be read',
  },
  resolutionsTitle: 'How each code was resolved',
  resolutionBy: (byRule: boolean, decidedBy: string | null): string =>
    byRule ? 'matched by code' : decidedBy ? `decided by ${decidedBy}` : 'decided',
  approve: 'Approve and create',
  approveConfirmTitle: (count: number): string => `Create ${String(count)} draft${count === 1 ? '' : 's'}?`,
  approveConfirmBody: 'Each draft is created on its own. Nobody is named and nothing is published.',
  needsAcknowledgement: 'Acknowledge or discard every already-produced template before approving.',
  nothingToCreate: 'Nothing in this plan can be created.',
  stale:
    'Something changed since this plan was worked out. It has been worked out again — check it and approve once more.',
  replan: 'Work it out again',
} as const;

export const PUBLISH_COPY = {
  title: (label: string): string => `Publish ${label} drafts`,
  checking: (done: number, total: number): string => `Checking drafts… ${String(done)} of ${String(total)}`,
  loading: 'Reading the drafts…',
  readyTitle: 'Ready to publish',
  heldTitle: 'In-charge not named — held back',
  heldBody: 'Name an officer for every in-charge role on these, or remove the role, and they become publishable.',
  blockedTitle: 'Other blockers',
  /** [DP2026] A typed draft made from no booklet — a corrigendum or a copy — is published from its own page. */
  unlinkedTitle: 'Not made from a booklet',
  unlinkedBody: 'These carry the type but were not created from a template. Publish each from its own page.',
  none: 'None.',
  noDrafts: (label: string): string => `There are no ${label} drafts to publish.`,
  approve: (count: number): string => `Publish ${String(count)} draft${count === 1 ? '' : 's'}`,
  approveConfirmTitle: (count: number): string => `Publish ${String(count)} draft${count === 1 ? '' : 's'}?`,
  approveConfirmBody:
    'Each is published on its own, by the ordinary publish. The divisions are asked to fill them; nobody is named.',
  recheck: 'Check again',
} as const;

export const RUN_COPY = {
  progress: (done: number, total: number): string => `${String(done)} of ${String(total)} done`,
  running: 'Working — keep this page open. Closing it pauses the run; opening it again resumes.',
  leaseHeld: 'This run is being advanced in another window. This page is following it.',
  done: 'Finished.',
  failedRequest: 'The run stopped:',
  retry: 'Continue',
  reportTitle: 'Report',
  counts: (created: number, already: number, failed: number, skipped: number): string =>
    `${String(created)} done · ${String(already)} already done · ${String(failed)} failed · ${String(skipped)} skipped`,
  open: 'Open',
  approvedBy: (name: string, at: string): string => `Approved by ${name}, ${at}`,
} as const;

/** A printed total's title, shared by its heading and by the subject of the links that name it. */
const TEMPLATE_VIEW_CHECK_TITLE = (page: number | null): string =>
  page === null ? 'Total row' : `Total row on page ${String(page)}`;

export const TEMPLATE_VIEW_COPY = {
  readOnly: 'A template is read-only. What it creates is an ordinary draft, reviewed before it is published.',
  crumbs: 'Where this template sits',
  pages: (pages: readonly number[]): string =>
    pages.length === 0 ? '' : `Booklet ${pages.length === 1 ? 'p.' : 'pp.'} ${pages.join(', ')}`,
  dutyPoints: (count: number): string => `${String(count)} duty point${count === 1 ? '' : 's'}`,
  proposed: 'proposed',
  info: 'Information',
  instructions: 'Instructions',
  notes: 'Other lines printed with this template',
  supervision: 'Command',
  groups: 'Duty points',
  zone: (zone: string): string => `Zone ${zone}`,
  sector: (sector: string): string => `Sector ${sector}`,
  sl: 'Sl',
  dutyPoint: 'Duty point',
  noPlace: 'No place printed',
  placeIssue: {
    MISSING: 'The booklet gives no place.',
    DAMAGED: 'The place was damaged when names were removed.',
    TOO_LONG: 'The place is longer than 200 characters.',
  },
  reportingPlace: 'Reporting place',
  inCharge: 'In-charge',
  other: 'Other',
  pageColumn: 'Page',
  reportTo: 'Report to',
  remarks: 'Remarks',
  /** [TEMPLATE-FIND] Takes the anchor's phrase — `duty point (1)`, `duty point at table 1, row 29` — never its key. */
  continues: (dutyPoint: string): string => `the same officer as ${dutyPoint}`,
  unread: (cell: string): string => `Not read: ${cell}`,
  roundTheClock: 'Round the clock — not built',
  forceRequired: 'Force required',
  forceCaption: 'People per source and column, as the booklet reads. In-charges are roles, never counted here.',
  forceSomeRoundTheClock: 'Duty points round the clock are not built and are not counted.',
  forceRoundTheClock: 'This template is round the clock and is never built — the booklet’s own figures, for reference.',
  checks: 'Totals check',
  checkTitle: TEMPLATE_VIEW_CHECK_TITLE,
  printed: 'Printed',
  read: 'Read',
  readFrom: (count: number): string => `Read from ${String(count)} row${count === 1 ? '' : 's'}`,
  /** [TEMPLATE-FIND] What a Totals check compares, said once above the checks. */
  checksCaption:
    'Each printed Total row is compared with the sum of the rows PAMS read above it. Where they differ, the Decisions page asks which to use.',
  /** [TEMPLATE-FIND] The rows one printed Total adds up: `Adds up table 1, rows 1–4`. */
  checkRows: (rows: string): string => `Adds up ${rows}`,
  /**
   * [TEMPLATE-FIND] A printed total as the subject of a link's accessible name. Two Total rows on one page
   * (`p78:t36`, `p78:t36#2`) share a title, so the rows each adds up are what tells them apart.
   */
  checkSubject: (page: number | null, rows: string): string =>
    rows === '' ? TEMPLATE_VIEW_CHECK_TITLE(page) : `${TEMPLATE_VIEW_CHECK_TITLE(page)}, adding up ${rows}`,
  /** [TEMPLATE-FIND] The template's number, first on its meta line — the number the Decisions page names it by. */
  number: (key: string): string => `Template ${key}`,
  /** [TEMPLATE-FIND] Each duty-point table's heading, numbered as a duty point's `table N, row M` counts it. */
  table: (table: number): string => `Table ${String(table)}`,
  /** [TEMPLATE-FIND] The first column: each row's number within its table. */
  row: 'Row',
  /** [TEMPLATE-FIND] The words beside a row or a check a link pointed at — never colour alone. */
  linkedPoint: 'You followed a link to this duty point.',
  linkedCheck: 'You followed a link to this printed total.',
  /**
   * [TEMPLATE-FIND] From an open duty point or a differing printed total back to its decision. ⚠️ The accessible
   * name STARTS with the visible words (WCAG 2.5.3, row 1010's rule) — never `Decide <what> on the …`.
   */
  decide: 'Decide on the Decisions page',
  decideLabel: (what: string): string => `Decide on the Decisions page: ${what}`,
  differs: 'Differs',
  matches: 'Matches',
  notCompared: 'Not compared',
  reviewNotes: 'Review notes',
  produced: 'Orders it produced',
  nothingProduced: 'It has produced no order yet.',
  noSource: 'no source',
} as const;

/** [P7B.1 §2] The force matrix's words — the review site's own headings. */
export const MATRIX_COPY = {
  source: 'Source',
  total: 'Total',
  noSource: 'No source printed',
  none: 'No force counts were read for this.',
  openColumn: 'Not matched yet — decide it on the decisions page.',
  openSource: 'Not in PAMS yet — decide it on the decisions page.',
} as const;
